import { useState, useEffect, useCallback } from 'react';
import { retainersAPI, clientsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, ArrowLeft, CalendarClock, CheckCircle2, Pencil, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { EditRetainerDialog } from '../components/retainers/EditRetainerDialog';
import { EditMonthDialog } from '../components/retainers/EditMonthDialog';

const SEGMENTS = ['Video', 'Design', 'Social Media', 'Branding', 'Website', 'Events', 'Gifting', 'AI Work', 'Creative Strategy', 'Others'];

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  overdue: 'bg-red-100 text-red-700',
  invoice_raised: 'bg-green-100 text-green-700',
  'N/A': 'bg-gray-100 text-gray-500',
};

const statusLabels = {
  pending: 'Pending',
  overdue: 'Overdue',
  invoice_raised: 'Invoice Raised',
  'N/A': 'N/A',
};

const ordinal = (n) => {
  if (n >= 11 && n <= 13) return 'th';
  return { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
};

const Retainers = ({ user }) => {
  const [retainers, setRetainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRetainer, setSelectedRetainer] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '', client_name: '', project_name: '', segment: 'Video',
    monthly_amount: '', invoice_day: '', start_date: '', end_date: '',
  });
  const [editMonth, setEditMonth] = useState(null);

  const fetchRetainers = useCallback(async () => {
    try {
      const res = await retainersAPI.getAll();
      setRetainers(res.data);
    } catch (error) {
      console.error('Failed to fetch retainers:', error);
      toast.error('Failed to fetch retainers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, []);

  useEffect(() => { fetchRetainers(); }, [fetchRetainers]);

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await retainersAPI.getOne(id);
      setDetailData(res.data);
      setSelectedRetainer(id);
    } catch (error) {
      console.error('Failed to fetch retainer details:', error);
      toast.error('Failed to fetch retainer details');
    } finally { setDetailLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await retainersAPI.create({
        ...formData,
        monthly_amount: parseFloat(formData.monthly_amount),
        invoice_day: parseInt(formData.invoice_day),
        end_date: formData.end_date || null,
      });
      toast.success('Retainer created successfully');
      setCreateOpen(false);
      resetForm();
      fetchRetainers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create retainer');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await retainersAPI.delete(deleteTarget.id);
      toast.success('Retainer deleted successfully');
      setDeleteOpen(false);
      setDeleteTarget(null);
      if (selectedRetainer === deleteTarget.id) {
        setSelectedRetainer(null);
        setDetailData(null);
      }
      fetchRetainers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete retainer');
    }
  };

  const handleStatusUpdate = async (monthId, newStatus) => {
    try {
      await retainersAPI.updateMonthStatus(monthId, {
        status: newStatus,
        invoice_raised_date: newStatus === 'invoice_raised' ? new Date().toISOString().split('T')[0] : null,
      });
      toast.success(newStatus === 'invoice_raised' ? 'Invoice raised' : 'Reverted to pending');
      fetchDetail(selectedRetainer);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update status');
    }
  };

  const openEditDialog = () => {
    if (!detailData) return;
    if (canEditAll) fetchClients();
    setEditOpen(true);
  };

  const refreshDetail = () => { fetchDetail(selectedRetainer); fetchRetainers(); };

  const openDeleteFromList = (e, retainer) => {
    e.stopPropagation();
    setDeleteTarget(retainer);
    setDeleteOpen(true);
  };

  const resetForm = () => {
    setFormData({ client_id: '', client_name: '', project_name: '', segment: 'Video',
      monthly_amount: '', invoice_day: '', start_date: '', end_date: '' });
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({ ...formData, client_id: clientId, client_name: client?.name || '' });
  };

  const currentMonth = new Date().toISOString().slice(0, 7);
  const canCreate = user?.role === 'manager' || user?.role === 'admin';
  const canEditAll = ['admin', 'accounts', 'signing_authority'].includes(user?.role);

  // Detail View
  if (selectedRetainer && detailData) {
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="retainer-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => { setSelectedRetainer(null); setDetailData(null); }} data-testid="retainer-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {detailData.project_name}
              </h1>
              <p className="text-muted-foreground mt-1">{detailData.client_name} &middot; {detailData.segment}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openEditDialog} data-testid="edit-retainer-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            <Button variant="destructive" onClick={() => { setDeleteTarget(detailData); setDeleteOpen(true); }} data-testid="delete-retainer-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Monthly Amount</p>
            <p className="text-2xl font-bold mt-1">&#8377;{detailData.monthly_amount?.toLocaleString('en-IN')}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Invoice Day</p>
            <p className="text-2xl font-bold mt-1">{detailData.invoice_day}<sup>{ordinal(detailData.invoice_day)}</sup></p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Start Date</p>
            <p className="text-2xl font-bold mt-1">{detailData.start_date}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">End Date</p>
            <p className="text-2xl font-bold mt-1">{detailData.end_date || 'Ongoing'}</p>
          </CardContent></Card>
        </div>

        <Card data-testid="retainer-months-table">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">Monthly Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-semibold">Month</th>
                    <th className="text-left p-3 font-semibold">Due Date</th>
                    <th className="text-left p-3 font-semibold">Amount</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Invoice Raised Date</th>
                    <th className="text-left p-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.months?.map((m) => {
                    const isCurrent = m.month === currentMonth;
                    return (
                      <tr key={m.id} className={`border-b transition-colors ${isCurrent ? 'bg-blue-50/60 ring-1 ring-blue-200' : 'hover:bg-muted/30'}`} data-testid={`month-row-${m.month}`}>
                        <td className="p-3 font-medium">
                          {m.month_label}
                          {isCurrent && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Current</span>}
                        </td>
                        <td className="p-3">{m.due_date}</td>
                        <td className="p-3">&#8377;{m.amount?.toLocaleString('en-IN')}</td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[m.status] || statusColors['N/A']}`}>
                            {statusLabels[m.status] || m.status}
                          </span>
                        </td>
                        <td className="p-3">{m.invoice_raised_date || '-'}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            {canEditAll && (
                              <Button size="sm" variant="ghost" onClick={() => setEditMonth(m)} data-testid={`edit-month-${m.month}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {(m.status === 'pending' || m.status === 'overdue') && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(m.id, 'invoice_raised')} data-testid={`raise-invoice-${m.month}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Raise Invoice
                              </Button>
                            )}
                            {m.status === 'invoice_raised' && (
                              <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => handleStatusUpdate(m.id, 'pending')} data-testid={`revert-status-${m.month}`}>
                                <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Revert
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <EditRetainerDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          retainer={detailData}
          canEditAll={canEditAll}
          clients={clients}
          segments={SEGMENTS}
          onSaved={refreshDetail}
        />
        <EditMonthDialog month={editMonth} onClose={() => setEditMonth(null)} onSaved={refreshDetail} />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="max-w-sm" data-testid="delete-retainer-dialog">
            <DialogHeader>
              <DialogTitle>Delete Retainer</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.project_name}</strong>? This will permanently remove the retainer and all its monthly records. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null); }}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  if (loading) return <div className="text-center py-12">Loading retainers...</div>;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="retainers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Retainers
          </h1>
          <p className="text-muted-foreground mt-2">Manage recurring retainer projects</p>
        </div>
        {canCreate && (
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (open) fetchClients(); else resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="create-retainer-btn">
                <Plus className="h-4 w-4" /> New Retainer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="create-retainer-dialog">
              <DialogHeader>
                <DialogTitle>Create Retainer</DialogTitle>
                <DialogDescription>Set up a new recurring retainer project</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.client_id} onChange={(e) => handleClientChange(e.target.value)} required data-testid="retainer-client-select">
                    <option value="">Select Client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} required data-testid="retainer-project-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Segment *</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.segment} onChange={(e) => setFormData({ ...formData, segment: e.target.value })} data-testid="retainer-segment-select">
                      {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Amount (INR) *</Label>
                    <Input type="number" min="0" step="0.01" value={formData.monthly_amount} onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })} required data-testid="retainer-amount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Invoice Day (1-28) *</Label>
                    <Input type="number" min="1" max="28" value={formData.invoice_day} onChange={(e) => setFormData({ ...formData, invoice_day: e.target.value })} required data-testid="retainer-day-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required data-testid="retainer-start-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} data-testid="retainer-end-input" />
                    <p className="text-xs text-muted-foreground">Leave blank for ongoing</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="retainer-submit-btn">Create Retainer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {retainers.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">No retainers yet.{canCreate ? ' Create your first retainer to get started.' : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-semibold">Client Name</th>
                    <th className="text-left p-4 font-semibold">Project Name</th>
                    <th className="text-left p-4 font-semibold">Monthly Amount</th>
                    <th className="text-left p-4 font-semibold">Next Due Date</th>
                    <th className="text-left p-4 font-semibold">Current Month Status</th>
                    <th className="text-left p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {retainers.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => fetchDetail(r.id)} data-testid={`retainer-row-${r.id}`}>
                      <td className="p-4 font-medium">{r.client_name}</td>
                      <td className="p-4">{r.project_name}</td>
                      <td className="p-4">&#8377;{r.monthly_amount?.toLocaleString('en-IN')}</td>
                      <td className="p-4">
                        {r.next_due_date ? (
                          <span className="flex items-center gap-1.5">
                            <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                            {r.next_due_date}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[r.current_month_status] || statusColors['N/A']}`}>
                          {statusLabels[r.current_month_status] || r.current_month_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => openDeleteFromList(e, r)} data-testid={`delete-list-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog (from list view) */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-retainer-dialog">
          <DialogHeader>
            <DialogTitle>Delete Retainer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.project_name}</strong>? This will permanently remove the retainer and all its monthly records. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Retainers;
